/**
 * RecruitingToolkit — Iter-290c (#4 + #1)
 *
 * Client-side dashboard rendered at /client/recruiting-toolkit.
 * Surfaces the company recruiting link with a QR code, copy/email/share
 * tools, suggested captions, and a small analytics summary card so
 * clients can see views/shares/applications/conversion at a glance.
 */
import React, { useEffect, useState } from 'react';
import { Copy, Mail, Download, Share2, BarChart3, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import ShareCardPreviewButton from '../../components/ShareCardPreviewButton';

const SUGGESTED_CAPTIONS = [
  "We're hiring! Check out our open opportunities on iEvntür — apply in 60 seconds. {link}",
  "Looking for outgoing brand ambassadors for upcoming activations. See our active openings and apply directly: {link}",
  "Event staffing gig opportunities — paid, flexible, fun. Tap to view all our open shifts on iEvntür: {link}",
  "Tag a friend who'd love this 👀 Apply to our open roles on iEvntür → {link}",
];

function qrUrl(text, size = 240) {
  // Google Charts has been deprecated; use the free chart server backed by
  // the QR Server project (api.qrserver.com) as a CDN fallback. No keys.
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}`;
}

export default function RecruitingToolkit() {
  const [slug, setSlug] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const me = await api.get('/auth/me');
        const role = me.data?.user?.role;
        if (role === 'client') {
          const cp = await api.get('/client/profile').catch(() => null);
          const s = cp?.data?.profile?.recruiting_slug || cp?.data?.recruiting_slug;
          if (!cancel) setSlug(s || null);
        }
        const ax = await api.get('/admin/jobs/analytics/summary').catch(() => null);
        if (!cancel) setAnalytics(ax?.data || null);
      } catch (_e) {
        // non-fatal
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return <div className="p-10 text-slate-500 text-sm flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading toolkit…</div>;
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const recruitingUrl = slug ? `${origin}/c/${slug}` : null;
  // Shared/copied/caption links use the `/api/l/company/:slug` resolver so
  // social crawlers get a personalised OG preview, then redirect humans to
  // the clean `/c/:slug` hub. Display/QR/Preview keep the clean URL.
  const shareRecruitingUrl = slug ? `${origin}/api/l/company/${slug}` : null;

  const handleCopy = (text, label = 'link') => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`Copied ${label}`),
      () => toast.error('Copy failed'),
    );
  };

  const handleEmail = () => {
    const subject = encodeURIComponent('Apply to our open roles on iEvntür');
    const body = encodeURIComponent(`Hi,\n\nWe're actively hiring — here are our open opportunities on iEvntür: ${shareRecruitingUrl}\n\nApply in one click. We'd love to have you on the team!\n\nThanks`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const downloadQr = async () => {
    if (!recruitingUrl) return;
    try {
      const r = await fetch(qrUrl(recruitingUrl, 512));
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ievntur-recruiting-qr-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed — right-click the QR to save it manually');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="recruiting-toolkit-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Recruiting Toolkit</h1>
        <p className="text-sm text-slate-500 mt-1">Share your branded recruiting page with one tap — and track what&rsquo;s working.</p>
      </div>

      {!recruitingUrl && (
        <Card data-testid="toolkit-no-slug">
          <CardContent className="py-6 text-sm text-slate-600">No recruiting link yet. Complete your company profile to unlock this toolkit.</CardContent>
        </Card>
      )}

      {recruitingUrl && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your recruiting link</CardTitle>
            <CardDescription>Share this single URL anywhere — Facebook groups, LinkedIn, SMS, your bio.</CardDescription>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-[240px_1fr] gap-5 items-start">
            <div className="flex flex-col items-center gap-2">
              <img src={qrUrl(recruitingUrl)} alt="Recruiting QR" className="rounded-xl border border-slate-200 bg-white p-2" data-testid="toolkit-qr-img" />
              <Button variant="outline" size="sm" onClick={downloadQr} data-testid="toolkit-qr-download"><Download className="w-4 h-4 mr-1.5" /> Download QR</Button>
            </div>
            <div className="space-y-3">
              <div className="flex items-stretch gap-2 flex-wrap">
                <div className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm font-mono truncate" data-testid="toolkit-url">{recruitingUrl}</div>
                <Button variant="outline" onClick={() => handleCopy(shareRecruitingUrl, 'recruiting link')} data-testid="toolkit-copy"><Copy className="w-4 h-4 mr-1.5" /> Copy</Button>
                <Button variant="outline" onClick={handleEmail} data-testid="toolkit-email"><Mail className="w-4 h-4 mr-1.5" /> Email</Button>
                <Button variant="outline" onClick={() => window.open(recruitingUrl, '_blank', 'noopener')} data-testid="toolkit-open"><ExternalLink className="w-4 h-4 mr-1.5" /> Preview</Button>
                <ShareCardPreviewButton
                  imageUrl={`${origin}/api/og/v/c3/company/${slug}.png`}
                  label="See my share card"
                  fileName={`ievntur-${slug}-share-card.png`}
                  testid="toolkit-share-card-preview"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'facebook', label: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareRecruitingUrl)}` },
                  { id: 'linkedin', label: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareRecruitingUrl)}` },
                  { id: 'x',        label: 'X',        url: `https://twitter.com/intent/tweet?text=${encodeURIComponent("We're hiring — apply on iEvntür: " + shareRecruitingUrl)}` },
                  { id: 'sms',      label: 'SMS',      url: `sms:?&body=${encodeURIComponent("We're hiring — apply on iEvntür: " + shareRecruitingUrl)}` },
                ].map((s) => (
                  <Button key={s.id} variant="outline" size="sm" onClick={() => window.open(s.url, '_blank', 'noopener')} data-testid={`toolkit-share-${s.id}`}>
                    <Share2 className="w-3.5 h-3.5 mr-1.5" /> {s.label}
                  </Button>
                ))}
              </div>
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Suggested captions</div>
                {SUGGESTED_CAPTIONS.map((c, i) => {
                  const text = c.replace('{link}', shareRecruitingUrl);
                  return (
                    <div key={i} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700" data-testid={`toolkit-caption-${i}`}>
                      <div className="flex-1 leading-relaxed">{text}</div>
                      <Button variant="ghost" size="sm" onClick={() => handleCopy(text, 'caption')} data-testid={`toolkit-caption-copy-${i}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-slate-400">Coming soon · downloadable recruiting flyer (PDF) generated from your branding.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {analytics && (
        <Card data-testid="toolkit-analytics-card">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-cyan-600" /> Recruiting analytics</CardTitle>
              <CardDescription>Total views, shares, and applications across your jobs.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Total views" value={analytics.totals?.views ?? 0} testid="analytics-views" />
              <Stat label="Total shares" value={analytics.totals?.shares ?? 0} testid="analytics-shares" />
              <Stat label="Applications" value={analytics.totals?.applications ?? 0} testid="analytics-apps" />
              <Stat label="Conversion" value={`${analytics.totals?.conversion_rate_pct ?? 0}%`} testid="analytics-conv" />
            </div>
            <div className="text-sm text-slate-600">
              {analytics.best_platform ? <>Best performing platform: <strong className="capitalize text-slate-900">{analytics.best_platform}</strong></> : 'No share data yet — start sharing to see insights here.'}
            </div>
            {analytics.top_jobs?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Most viewed jobs</div>
                <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden" data-testid="analytics-top-jobs">
                  {analytics.top_jobs.map((j) => (
                    <li key={j.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="truncate pr-3">{j.title || 'Untitled'}</span>
                      <span className="text-xs text-slate-500 shrink-0">{j.views} views · {j.shares} shares · {j.applications} apps</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 px-3 py-3 bg-gradient-to-br from-white to-slate-50" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
